import React, { useState, useRef, useEffect } from 'react';
import GoogleMapsLoader from '../components/GoogleMapsLoader';
import DirectionsMap from '../components/DirectionsMap';
import DirectionsPanel from '../components/DirectionsPanel';
import { MapPin, X, Search, Navigation } from 'lucide-react';

export default function GPS() {
  const [destination, setDestination] = useState<google.maps.LatLngLiteral | null>(null);
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [isDirectionsPanelExpanded, setIsDirectionsPanelExpanded] = useState(true);
  const [currentStreet, setCurrentStreet] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<any>(null);

  // Set up address autocomplete with improved options
  useEffect(() => {
    if (!addressInputRef.current || !window.google?.maps?.places) return;
    
    try {
      // Clear existing listeners to prevent duplicates
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      
      // Create the autocomplete instance with more fields for accuracy
      autocompleteRef.current = new google.maps.places.Autocomplete(addressInputRef.current, {
        fields: ['formatted_address', 'geometry', 'name', 'address_components'],
        componentRestrictions: { country: 'us' } // Adjust this for your region
      });
      
      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();
        
        if (!place || !place.geometry || !place.geometry.location) {
          setError('Please select a valid address from the dropdown');
          return;
        }
        
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        
        setDestination(location);
        if (place.formatted_address) {
          setAddress(place.formatted_address);
        }
        setError(null);
        
        // Auto-start navigation if requested
        if (autoStartRef.current) {
          setIsNavigating(true);
          autoStartRef.current = false;
        }
      });
    } catch (err) {
      console.error('Error setting up autocomplete:', err);
      setError('Failed to initialize address search');
    }
    
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [addressInputRef.current, window.google?.maps?.places]);
  
  // Reference to track if we should auto-start navigation
  const autoStartRef = useRef<boolean>(false);
  
  const handleStartNavigation = () => {
    if (!destination) {
      setError('Please enter a destination address first');
      return;
    }
    
    setIsNavigating(true);
  };
  
  const handleStopNavigation = () => {
    setIsNavigating(false);
  };
  
  const handleClearDestination = () => {
    setDestination(null);
    setAddress('');
    setIsNavigating(false);
    setDirections(null);
    setCurrentStep(0);
  };

  const handleDirectionsCalculated = (result: google.maps.DirectionsResult | null) => {
    setDirections(result);
    
    // Reset current step when directions change
    setCurrentStep(0);
  };
  
  // Set up map ref callback to receive map instance updates
  const handleMapInstanceUpdate = (mapInstance: any) => {
    mapRef.current = mapInstance;
  };
  
  // Update current street from map component
  const handleStreetUpdate = (street: string | null) => {
    setCurrentStreet(street);
  };
  
  // Update current step from map
  const handleStepUpdate = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-900">GPS Navigation</h1>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5] overflow-hidden flex flex-col flex-1 relative">
        {/* Map Container */}
        <div className="flex-1 relative">
          <GoogleMapsLoader apiKey="AIzaSyCGC4-0ZxPH8WLgxMYBhVmyscFSxk0Yhx8">
            <DirectionsMap 
              destination={isNavigating ? destination : null} 
              className="h-full w-full"
              onDirectionsCalculated={handleDirectionsCalculated}
              onMapInstance={handleMapInstanceUpdate}
              onStreetUpdate={handleStreetUpdate}
              onStepUpdate={handleStepUpdate}
            />
          </GoogleMapsLoader>
          
          {/* Floating Nav Bar */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-white/90 backdrop-blur-sm shadow-md p-3 border-b border-gray-200">
            {isNavigating ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleStopNavigation}
                  className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex-1 truncate px-2">
                  <div className="text-sm font-medium text-gray-900 truncate">{address}</div>
                  {currentStreet && (
                    <div className="text-xs text-gray-600 truncate">
                      Currently on: {currentStreet}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="p-2 bg-gray-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-gray-700" />
                </div>
                <div className="relative flex-1">
                  <input
                    ref={addressInputRef}
                    type="text"
                    placeholder="Enter destination address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full h-10 pl-3 pr-8 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                  {address && (
                    <button 
                      onClick={handleClearDestination}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {destination && (
                  <button
                    onClick={handleStartNavigation}
                    className="p-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 flex items-center justify-center"
                  >
                    <Navigation className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Error message */}
          {error && (
            <div className="absolute top-16 left-0 right-0 mx-auto max-w-md px-4 z-10">
              <div className="mt-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            </div>
          )}
          
          {/* Directions Panel - Floating above map */}
          {directions && (
            <div className="absolute bottom-4 left-0 right-0 mx-auto w-full max-w-md px-4 z-10">
              <DirectionsPanel 
                directions={directions}
                isExpanded={isDirectionsPanelExpanded}
                onToggleExpand={() => setIsDirectionsPanelExpanded(!isDirectionsPanelExpanded)}
                isNavigating={isNavigating}
                onStartNavigation={handleStartNavigation}
                onStopNavigation={handleStopNavigation}
                currentStreet={currentStreet}
                currentStep={currentStep}
              />
            </div>
          )}
          
          {/* Quick navigation button */}
          {!isNavigating && !directions && (
            <div className="absolute bottom-4 right-4 z-10">
              <button 
                onClick={() => {
                  if (navigator.geolocation) {
                    // Example: Navigate to a predefined location or recent destination
                    autoStartRef.current = true;
                    addressInputRef.current?.focus();
                  }
                }}
                className="bg-gray-900 text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
                title="Quick navigate"
              >
                <Navigation className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 